import React, { useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { BACKEND_URL } from "@/lib/api";

const img = (id) => `${BACKEND_URL}/api/fresh-start/gallery/${id}.jpg`;

export const useFreshStartPairs = () => {
  const [pairs, setPairs] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND_URL}/api/fresh-start/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => alive && setPairs(c?.pairs || []))
      .catch(() => alive && setPairs([]));
    return () => { alive = false; };
  }, []);
  return pairs;
};

// Swipeable before/after slideshow fed by the Fresh Start gallery.
export const BeforeAfterSlideshow = ({ pairs, accent = "#C84B31" }) => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const track = useRef(null);
  const n = pairs.length;

  useEffect(() => {
    if (n < 2 || paused) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  }, [i]);

  const scrollT = useRef(null);
  const onScroll = () => {
    clearTimeout(scrollT.current);
    scrollT.current = setTimeout(() => {
      const el = track.current;
      if (!el) return;
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      if (idx >= 0 && idx < n) setI((v) => (v === idx ? v : idx));
    }, 150);
  };

  const go = (d) => { setPaused(true); setI((v) => (v + d + n) % n); };

  return (
    <div
      data-testid="before-after-slideshow"
      className="relative"
      onTouchStart={() => setPaused(true)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={track}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-roledescription="carousel"
        aria-label="Before and after transformations"
      >
        {pairs.map((p, idx) => (
          <figure
            key={p.pair_id}
            data-testid={`before-after-slide-${idx + 1}`}
            aria-hidden={idx !== i}
            className="m-0 w-full shrink-0 snap-center"
          >
            <div className="grid grid-cols-2 gap-[3px] overflow-hidden rounded-sm bg-[#F5F4F0]/20">
              {[["before", p.before_id, "BEFORE"], ["after", p.after_id, "AFTER"]].map(([k, id, label]) => (
                <div key={k} className="relative">
                  <img
                    src={img(id)}
                    alt={`${label === "BEFORE" ? "Before" : "After"}: ${p.caption || "Fresh Start transformation"}`}
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <span
                    className="absolute left-2 top-2 rounded-sm px-2 py-1 font-mono-label text-[11px] tracking-[0.2em] text-[#F5F4F0]"
                    style={{ backgroundColor: label === "AFTER" ? accent : "rgba(28,26,23,0.85)" }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
            {p.caption && (
              <figcaption className="mt-3 text-[13px] text-[#F5F4F0]/75">{p.caption}</figcaption>
            )}
          </figure>
        ))}
      </div>

      {n > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="before-after-dots">
            {pairs.map((p, idx) => (
              <button
                key={p.pair_id}
                type="button"
                aria-label={`Go to transformation ${idx + 1}`}
                aria-current={idx === i}
                onClick={() => { setPaused(true); setI(idx); }}
                className="grid h-11 w-6 place-items-center"
              >
                <span
                  className="block h-1.5 rounded-full transition-all duration-300"
                  style={{ width: idx === i ? 24 : 8, backgroundColor: idx === i ? accent : "rgba(245,244,240,0.35)" }}
                />
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => go(-1)} aria-label="Previous" data-testid="before-after-prev"
              className="grid h-11 w-11 place-items-center rounded-sm border border-[#F5F4F0]/30 text-[#F5F4F0] transition-colors hover:bg-[#F5F4F0] hover:text-[#1B2A22]">
              <CaretLeft size={18} weight="bold" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next" data-testid="before-after-next"
              className="grid h-11 w-11 place-items-center rounded-sm border border-[#F5F4F0]/30 text-[#F5F4F0] transition-colors hover:bg-[#F5F4F0] hover:text-[#1B2A22]">
              <CaretRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
